/**
 * NewAITools.jsx — 5 new AI tool components
 * Self-contained — useAITool hook is included in this file,
 * no separate import needed.
 *
 * Add to TOOLS array in AITools.jsx:
 *   { id:'upwork',    label:'Upwork Proposal',  icon:'📝' },
 *   { id:'recruiter', label:'Recruiter Reply',  icon:'💼' },
 *   { id:'job',       label:'Job Matcher',      icon:'🎯' },
 *   { id:'cron',      label:'Cron Generator',  icon:'⏰' },
 *   { id:'apitest',   label:'API Tester',       icon:'🔌' },
 *
 * Add to COMPONENTS map:
 *   upwork:    UpworkProposalGenerator,
 *   recruiter: RecruiterReplyGenerator,
 *   job:       JobDescriptionMatcher,
 *   cron:      CronGenerator,
 *   apitest:   APITester,
 */

import React, { useState, useCallback } from 'react';
import { useAuth } from './AuthContext';   // your existing AuthContext

/* ─── useAITool HOOK — inline, no separate file needed ───────────────── */
function useAITool(endpoint) {
  const { apiFetch } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');
  const [usage,   setUsage]   = useState({ used: 0, limit: 0 });

  const run = useCallback(async (body) => {
    setLoading(true); setError('');
    try {
      const res  = await apiFetch(`/api/tools/ai/${endpoint}/`, {
        method: 'POST',
        body:   JSON.stringify(body),
      });
      const data = await res.json();

      if (res.status === 429) {
        setError(data.error || 'Daily AI limit reached. Upgrade for more requests.');
        setUsage({ used: data.used, limit: data.limit });
        return null;
      }
      if (res.status === 403) {
        setError('AI Tools subscription required.');
        return null;
      }
      if (!res.ok) {
        setError(data.error || 'Request failed');
        return null;
      }

      if (data.usage) setUsage(data.usage);
      return data.text;
    } catch (e) {
      setError(e.message || 'Network error');
      return null;
    } finally {
      setLoading(false);
    }
  }, [endpoint, apiFetch]);

  return { run, loading, error, usage };
}

/* ─── UsageBadge — inline ─────────────────────────────────────────────── */
function UsageBadge({ usage }) {
  if (!usage || usage.limit === 0) return null;
  const pct   = usage.used / usage.limit;
  const color = pct >= 1 ? '#e74c3c' : pct >= 0.8 ? '#e67e22' : '#00c8f0';
  return (
    <div style={{
      display:'inline-flex', alignItems:'center', gap:6,
      padding:'4px 12px', borderRadius:20, marginBottom:12,
      background:`${color}12`, border:`1px solid ${color}30`,
      fontSize:12, color, fontFamily:'"DM Sans",system-ui,sans-serif', fontWeight:600,
    }}>
      {usage.used} / {usage.limit} AI requests today
    </div>
  );
}

/* ─── SHARED TOKENS ─────────────────────────────────────────────────────── */
const T = {
  bg:     '#07080c', bg2:'#0c0f18', bg3:'#111520', surf:'#161c2a',
  border: 'rgba(255,255,255,.07)', borderH:'rgba(255,255,255,.13)',
  white:  '#dce6fa', muted:'rgba(220,230,250,.45)', dim:'rgba(220,230,250,.22)',
  cyan:   '#00c8f0', blue:'#4d8fff', green:'#2ecc71',
  red:    '#e74c3c', yellow:'#f1c40f', purple:'#b78cff', orange:'#e67e22',
  sans:   '"Syne","DM Sans",system-ui,sans-serif',
  mono:   '"JetBrains Mono","Fira Code",monospace',
};

/* ─── SHARED STYLES ─────────────────────────────────────────────────────── */
const S = {
  textarea: {
    width:'100%', padding:'12px 14px',
    background:T.bg, border:`1px solid ${T.border}`,
    borderRadius:8, color:T.white,
    fontSize:13, fontFamily:T.mono, lineHeight:1.65,
    resize:'vertical', outline:'none', boxSizing:'border-box', minHeight:120,
  },
  input: {
    width:'100%', padding:'9px 12px',
    background:T.bg, border:`1px solid ${T.border}`,
    borderRadius:7, color:T.white,
    fontSize:13, fontFamily:T.mono, outline:'none', boxSizing:'border-box',
  },
  select: {
    width:'100%', padding:'9px 12px',
    background:T.bg, border:`1px solid ${T.border}`,
    borderRadius:7, color:T.white,
    fontSize:13, fontFamily:T.sans, outline:'none',
    boxSizing:'border-box', cursor:'pointer',
  },
  label: {
    display:'block', fontSize:11, fontWeight:700,
    textTransform:'uppercase', letterSpacing:'.08em',
    color:T.dim, marginBottom:6, fontFamily:T.sans,
  },
  output: {
    width:'100%', padding:'14px 16px',
    background:T.bg3, border:`1px solid ${T.border}`,
    borderRadius:8, color:T.white,
    fontSize:13, fontFamily:T.mono, lineHeight:1.7,
    whiteSpace:'pre-wrap', wordBreak:'break-word', minHeight:120,
  },
};

/* ─── SHARED ATOMS ──────────────────────────────────────────────────────── */
function Label({ children }) {
  return <label style={S.label}>{children}</label>;
}

function RunBtn({ onClick, loading, label = 'Generate', color = T.cyan }) {
  return (
    <button onClick={onClick} disabled={loading} style={{
      padding:'10px 24px', borderRadius:8,
      background: loading ? `${color}30` : `linear-gradient(135deg,${color},${T.blue})`,
      border:'none', color: color === T.cyan ? '#07080c' : T.white,
      fontSize:13.5, fontWeight:700, fontFamily:T.sans,
      cursor: loading ? 'default' : 'pointer',
      display:'inline-flex', alignItems:'center', gap:8, transition:'all .2s',
    }}>
      {loading && (
        <span style={{
          width:13, height:13, borderRadius:'50%',
          border:`2px solid rgba(0,0,0,.25)`, borderTopColor:'#07080c',
          display:'inline-block', animation:'nai-spin .65s linear infinite',
        }}/>
      )}
      {loading ? 'Generating…' : label}
    </button>
  );
}

function CopyBtn({ value }) {
  const [copied, setCopied] = useState(false);
  return (
    <button onClick={() => {
      navigator.clipboard?.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }} style={{
      padding:'5px 12px', borderRadius:6,
      border:`1px solid ${T.border}`, background:'transparent',
      color: copied ? T.green : T.muted,
      fontSize:12, fontFamily:T.sans, cursor:'pointer',
    }}>
      {copied ? '✓ Copied' : '⧉ Copy'}
    </button>
  );
}

function ErrorBox({ msg }) {
  if (!msg) return null;
  return (
    <div style={{
      padding:'9px 13px', borderRadius:7, marginTop:10,
      background:`${T.red}12`, border:`1px solid ${T.red}35`,
      fontSize:12.5, color:T.red,
    }}>{msg}</div>
  );
}

function OutputSection({ output, label = 'Output' }) {
  if (!output) return null;
  return (
    <div style={{ marginTop:16 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
        <Label>{label}</Label>
        <CopyBtn value={output}/>
      </div>
      <div style={S.output}>{output}</div>
    </div>
  );
}

function TwoCol({ children }) {
  return (
    <div className="nai-two-col" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
      {children}
    </div>
  );
}

/* Global keyframe + responsive CSS */
if (typeof document !== 'undefined' && !document.getElementById('nai-styles')) {
  const s = document.createElement('style');
  s.id = 'nai-styles';
  s.textContent = `
    @keyframes nai-spin { to { transform: rotate(360deg); } }
    .nai-two-col > * { min-width: 0; }
    input::placeholder, textarea::placeholder { color: rgba(220,230,250,.22) !important; }
    input:focus, textarea:focus, select:focus { border-color: rgba(0,200,240,.4) !important; outline: none; }
    @media (max-width: 640px) { .nai-two-col { grid-template-columns: 1fr !important; } }
  `;
  document.head.appendChild(s);
}


/* ══════════════════════════════════════════════════════════════════════════
   1. UPWORK PROPOSAL GENERATOR
══════════════════════════════════════════════════════════════════════════ */
export function UpworkProposalGenerator() {
  const [jobDesc,    setJobDesc]    = useState('');
  const [skills,     setSkills]     = useState('');
  const [experience, setExperience] = useState('');
  const [rate,       setRate]       = useState('');
  const [tone,       setTone]       = useState('professional');
  const [output,     setOutput]     = useState('');
  const { run, loading, error, usage } = useAITool('upwork-proposal');

  const handleGenerate = async () => {
    const text = await run({ job_description:jobDesc, skills, experience, rate, tone });
    if (text) setOutput(text);
  };

  return (
    <div>
      <UsageBadge usage={usage}/>
      <TwoCol>
        <div>
          <Label>Job Description (paste from Upwork)</Label>
          <textarea style={{ ...S.textarea, minHeight:160 }} value={jobDesc}
            onChange={e => setJobDesc(e.target.value)}
            placeholder="Paste the full Upwork job posting here…"/>
        </div>
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          <div>
            <Label>Your Skills</Label>
            <textarea style={{ ...S.textarea, minHeight:68 }} value={skills}
              onChange={e => setSkills(e.target.value)}
              placeholder="React, Node.js, Django, 5 years experience…"/>
          </div>
          <div>
            <Label>Relevant Experience</Label>
            <textarea style={{ ...S.textarea, minHeight:68 }} value={experience}
              onChange={e => setExperience(e.target.value)}
              placeholder="Built 3 similar SaaS apps, increased revenue 40%…"/>
          </div>
        </div>
      </TwoCol>
      <div style={{ display:'flex', gap:10, marginTop:10, flexWrap:'wrap', alignItems:'flex-end' }}>
        <div style={{ flex:1, minWidth:140 }}>
          <Label>Your Rate (optional)</Label>
          <input style={S.input} value={rate} onChange={e => setRate(e.target.value)} placeholder="$75/hr or $2,500 fixed"/>
        </div>
        <div style={{ flex:1, minWidth:140 }}>
          <Label>Tone</Label>
          <select style={S.select} value={tone} onChange={e => setTone(e.target.value)}>
            <option value="professional">Professional</option>
            <option value="friendly">Friendly & Approachable</option>
            <option value="confident">Confident & Direct</option>
            <option value="technical">Technical & Detailed</option>
          </select>
        </div>
        <RunBtn onClick={handleGenerate} loading={loading} label="Generate Proposal"/>
      </div>
      <ErrorBox msg={error}/>
      <OutputSection output={output} label="Your Proposal"/>
    </div>
  );
}


/* ══════════════════════════════════════════════════════════════════════════
   2. LINKEDIN RECRUITER REPLY
══════════════════════════════════════════════════════════════════════════ */
export function RecruiterReplyGenerator({ resume }) {
  const [message,   setMessage]   = useState('');
  const [situation, setSituation] = useState('ask_more');
  const [name,      setName]      = useState('');
  const [tone,      setTone]      = useState('professional');
  const [output,    setOutput]    = useState('');
  const { run, loading, error, usage } = useAITool('recruiter-reply');

  const SITUATIONS = [
    { value:'interested',     label:'✅ Interested — want to move forward',   color:T.green  },
    { value:'ask_more',       label:'🤔 Interested but need more info first',  color:T.cyan   },
    { value:'maybe',          label:'💭 Open to it but not actively looking',  color:T.yellow },
    { value:'not_interested', label:'❌ Not interested right now',             color:T.red    },
  ];

  const handleGenerate = async () => {
    const text = await run({ recruiter_message:message, situation, name, tone });
    if (text) setOutput(text);
  };

  return (
    <div>
      <UsageBadge usage={usage}/>
      <div style={{ marginBottom:12 }}>
        <Label>Recruiter's Message (paste from LinkedIn)</Label>
        <textarea style={{ ...S.textarea, minHeight:140 }} value={message}
          onChange={e => setMessage(e.target.value)}
          placeholder="Paste the recruiter's LinkedIn message here…"/>
      </div>
      <Label>My Situation</Label>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:14 }}>
        {SITUATIONS.map(s => (
          <button key={s.value} onClick={() => setSituation(s.value)} style={{
            padding:'10px 12px', borderRadius:8, textAlign:'left',
            border:`1px solid ${situation===s.value ? s.color+'60' : T.border}`,
            background: situation===s.value ? `${s.color}15` : 'transparent',
            color: situation===s.value ? s.color : T.muted,
            fontSize:12.5, fontFamily:T.sans, cursor:'pointer', transition:'all .15s',
          }}>{s.label}</button>
        ))}
      </div>
      <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'flex-end' }}>
        <div style={{ flex:1, minWidth:140 }}>
          <Label>Your Name (optional)</Label>
          <input style={S.input} value={name} onChange={e => setName(e.target.value)} placeholder="Rohit"/>
        </div>
        <div style={{ flex:1, minWidth:140 }}>
          <Label>Tone</Label>
          <select style={S.select} value={tone} onChange={e => setTone(e.target.value)}>
            <option value="professional">Professional</option>
            <option value="friendly">Friendly & Casual</option>
            <option value="brief">Brief & Direct</option>
          </select>
        </div>
        <RunBtn onClick={handleGenerate} loading={loading} label="Write Reply" color={T.blue}/>
      </div>
      <ErrorBox msg={error}/>
      <OutputSection output={output} label="Your Reply"/>
    </div>
  );
}


/* ══════════════════════════════════════════════════════════════════════════
   3. JOB DESCRIPTION MATCHER
══════════════════════════════════════════════════════════════════════════ */
export function JobDescriptionMatcher({ resume }) {
  const [jobDesc,  setJobDesc]  = useState('');
  const [profile,  setProfile]  = useState('');
  const [format,   setFormat]   = useState('analysis');
  const [output,   setOutput]   = useState('');
  const { run, loading, error, usage } = useAITool('job-matcher');

  const FORMATS = [
    { value:'analysis',     label:'📊 Match Analysis',   desc:'Score + strengths + gaps + recommendation' },
    { value:'cover_letter', label:'✉️ Cover Letter',      desc:'Targeted cover letter for this specific job' },
    { value:'keywords',     label:'🔑 Keyword Extractor', desc:'ATS keywords matched vs missing'            },
    { value:'gap_analysis', label:'🎯 Gap Analysis',      desc:'What you have vs what they need'            },
  ];

  const handleGenerate = async () => {
    const text = await run({ job_description:jobDesc, resume_or_skills:profile, output_format:format });
    if (text) setOutput(text);
  };

  return (
    <div>
      <UsageBadge usage={usage}/>
      <TwoCol>
        <div>
          <Label>Job Description</Label>
          <textarea style={{ ...S.textarea, minHeight:180 }} value={jobDesc}
            onChange={e => setJobDesc(e.target.value)}
            placeholder="Paste the full job description here…"/>
        </div>
        <div>
          <Label>Your Resume / Skills / Experience</Label>
          <textarea style={{ ...S.textarea, minHeight:180 }} value={profile?profile:resume?.raw_text || ''}
            onChange={e => setProfile(e.target.value)}
            placeholder="Paste your resume, LinkedIn summary, or list your skills and experience…"/>
        </div>
      </TwoCol>
      <div style={{ marginTop:12, marginBottom:14 }}>
        <Label>What do you need?</Label>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))', gap:8 }}>
          {FORMATS.map(f => (
            <button key={f.value} onClick={() => setFormat(f.value)} style={{
              padding:'10px 12px', borderRadius:8, textAlign:'left',
              border:`1px solid ${format===f.value ? T.cyan+'60' : T.border}`,
              background: format===f.value ? `${T.cyan}12` : 'transparent',
              color: format===f.value ? T.cyan : T.muted,
              fontSize:12.5, fontFamily:T.sans, cursor:'pointer', transition:'all .15s',
            }}>
              <div style={{ fontWeight:600 }}>{f.label}</div>
              <div style={{ fontSize:11, marginTop:2, color: format===f.value ? T.cyan+'90' : T.dim }}>{f.desc}</div>
            </button>
          ))}
        </div>
      </div>
      <RunBtn onClick={handleGenerate} loading={loading} label="Analyze Match" color={T.purple}/>
      <ErrorBox msg={error}/>
      <OutputSection output={output} label="Match Analysis"/>
    </div>
  );
}


/* ══════════════════════════════════════════════════════════════════════════
   4. CRON GENERATOR
══════════════════════════════════════════════════════════════════════════ */
export function CronGenerator() {
  const [description, setDescription] = useState('');
  const [timezone,    setTimezone]    = useState('UTC');
  const [format,      setFormat]      = useState('standard');
  const [output,      setOutput]      = useState('');
  const { run, loading, error, usage } = useAITool('cron');

  const handleGenerate = async () => {
    const text = await run({ description, timezone, format });
    if (text) setOutput(text);
  };

  const EXAMPLES = [
    'Every weekday at 9am',
    'Every 15 minutes during business hours',
    'First day of every month at midnight',
    'Every Sunday at 2am',
    'Twice a day at 6am and 6pm',
  ];

  const COMMON_TZ = [
    'UTC','America/Toronto','America/Vancouver','America/New_York',
    'America/Chicago','America/Los_Angeles','Europe/London',
    'Europe/Paris','Asia/Kolkata','Asia/Tokyo','Australia/Sydney',
  ];

  return (
    <div>
      <UsageBadge usage={usage}/>
      <div style={{ marginBottom:12 }}>
        <Label>Schedule Description (plain English)</Label>
        <input style={S.input} value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder="Every weekday at 9am… or First of every month at midnight…"/>
        <div style={{ display:'flex', gap:6, marginTop:8, flexWrap:'wrap' }}>
          {EXAMPLES.map(ex => (
            <button key={ex} onClick={() => setDescription(ex)} style={{
              padding:'4px 10px', borderRadius:20,
              border:`1px solid ${T.border}`, background:'transparent',
              color:T.muted, fontSize:11.5, fontFamily:T.sans, cursor:'pointer',
            }}>{ex}</button>
          ))}
        </div>
      </div>
      <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'flex-end' }}>
        <div style={{ flex:1, minWidth:160 }}>
          <Label>Timezone</Label>
          <select style={S.select} value={timezone} onChange={e => setTimezone(e.target.value)}>
            {COMMON_TZ.map(tz => <option key={tz} value={tz}>{tz}</option>)}
          </select>
        </div>
        <div style={{ flex:1, minWidth:160 }}>
          <Label>Format</Label>
          <select style={S.select} value={format} onChange={e => setFormat(e.target.value)}>
            <option value="standard">Standard (5-field)</option>
            <option value="quartz">Quartz (6-field with seconds)</option>
            <option value="aws">AWS EventBridge</option>
          </select>
        </div>
        <RunBtn onClick={handleGenerate} loading={loading} label="Generate Cron" color={T.orange}/>
      </div>
      <ErrorBox msg={error}/>
      <OutputSection output={output} label="Cron Expression"/>
    </div>
  );
}


/* ══════════════════════════════════════════════════════════════════════════
   5. API TESTER
══════════════════════════════════════════════════════════════════════════ */
export function APITester() {
  const [method,    setMethod]    = useState('GET');
  const [url,       setUrl]       = useState('');
  const [headers,   setHeaders]   = useState('');
  const [body,      setBody]      = useState('');
  const [resStatus, setResStatus] = useState('');
  const [resBody,   setResBody]   = useState('');
  const [question,  setQuestion]  = useState('');
  const [output,    setOutput]    = useState('');
  const [tab,       setTab]       = useState('request');
  const { run, loading, error, usage } = useAITool('api-tester');

  const handleAnalyze = async () => {
    const text = await run({
      method, url, headers, body,
      response_status: resStatus,
      response_body:   resBody,
      question,
    });
    if (text) setOutput(text);
  };

  const METHODS      = ['GET','POST','PUT','PATCH','DELETE','HEAD','OPTIONS'];
  const METHOD_COLOR = { GET:T.green, POST:T.cyan, PUT:T.yellow, PATCH:T.orange, DELETE:T.red };

  return (
    <div>
      <UsageBadge usage={usage}/>

      {/* Method + URL */}
      <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:12 }}>
        <select value={method} onChange={e => setMethod(e.target.value)} style={{
          ...S.select, width:'auto', minWidth:110,
          color: METHOD_COLOR[method] || T.white, fontWeight:700,
        }}>
          {METHODS.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
        <input style={{ ...S.input, flex:1 }} value={url}
          onChange={e => setUrl(e.target.value)}
          placeholder="https://api.example.com/endpoint"/>
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', borderBottom:`1px solid ${T.border}`, marginBottom:12 }}>
        {[
          { id:'request',  label:'Request'  },
          { id:'response', label:'Response' },
          { id:'question', label:'Ask AI'   },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding:'7px 16px', border:'none',
            borderBottom:`2px solid ${tab===t.id ? T.cyan : 'transparent'}`,
            background:'transparent', color: tab===t.id ? T.white : T.muted,
            fontSize:13, fontFamily:T.sans, cursor:'pointer', marginBottom:-1,
          }}>{t.label}</button>
        ))}
      </div>

      {tab === 'request' && (
        <TwoCol>
          <div>
            <Label>Headers (key: value, one per line)</Label>
            <textarea style={S.textarea} value={headers}
              onChange={e => setHeaders(e.target.value)}
              placeholder={"Authorization: Bearer token\nContent-Type: application/json"}/>
          </div>
          <div>
            <Label>Request Body (JSON or raw)</Label>
            <textarea style={S.textarea} value={body}
              onChange={e => setBody(e.target.value)}
              placeholder={'{\n  "key": "value"\n}'}/>
          </div>
        </TwoCol>
      )}

      {tab === 'response' && (
        <div style={{ display:'grid', gridTemplateColumns:'140px 1fr', gap:12 }}>
          <div>
            <Label>Status Code</Label>
            <input style={S.input} value={resStatus}
              onChange={e => setResStatus(e.target.value)}
              placeholder="404"/>
          </div>
          <div>
            <Label>Response Body</Label>
            <textarea style={S.textarea} value={resBody}
              onChange={e => setResBody(e.target.value)}
              placeholder={'{\n  "error": "Not found"\n}'}/>
          </div>
        </div>
      )}

      {tab === 'question' && (
        <div>
          <Label>Ask anything about this request/response</Label>
          <input style={S.input} value={question}
            onChange={e => setQuestion(e.target.value)}
            placeholder="Why am I getting a 401? / How do I paginate this? / What's wrong with my auth header?"/>
        </div>
      )}

      <div style={{ marginTop:14 }}>
        <RunBtn onClick={handleAnalyze} loading={loading} label="Analyze" color={T.green}/>
      </div>
      <ErrorBox msg={error}/>
      <OutputSection output={output} label="AI Analysis"/>
    </div>
  );
}